import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Instagram, Mail, Phone, MapPin, Send, Loader2, ArrowUp } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const footerText = {
  de: {
    projectQuestion: 'Have a project in mind?',
    letsTalk: "Let's Talk",
    description: 'Swiss Made Web Solutions – Professionelle Webseiten Schweiz, SEO und Online Marketing für KMU im DACH-Raum, Mallorca und Italien.',
    newsletter: 'Newsletter',
    newsletterDesc: 'Ab und zu teilen wir Tipps zu Marketing, Webseiten, Sichtbarkeit und Branding — verständlich erklärt und direkt anwendbar. Dazu Einblicke in Projekte, Trends und Tools, die wirklich helfen. Kurz gesagt: Nützlicher Stuff für dein Business. Kein Blabla. Kein tägliches Bombardement.',
    newsletterPlaceholder: 'Ihre E-Mail-Adresse',
    newsletterSuccess: 'Vielen Dank für Ihre Anmeldung!',
    newsletterError: 'Ein Fehler ist aufgetreten.',
    navigation: 'Navigation',
    contact: 'Kontakt',
    navLinks: {
      leistungen: 'Leistungen',
      projekte: 'Projekte',
      ueberUns: 'Über uns',
      kontakt: 'Kontakt',
      blog: 'Blog'
    },
    legal: {
      datenschutz: 'Datenschutz',
      impressum: 'Impressum',
      agb: 'AGB',
      cookies: 'Cookie-Richtlinie'
    }
  },
  it: {
    projectQuestion: 'Hai un progetto in mente?',
    letsTalk: "Let's Talk",
    description: 'Swiss Made Web Solutions – Siti web professionali Svizzera, SEO e marketing online per PMI in area DACH, Maiorca e Italia.',
    newsletter: 'Newsletter',
    newsletterDesc: 'Di tanto in tanto condividiamo consigli su marketing, siti web, visibilità e branding — spiegati in modo semplice e subito applicabili. Inoltre approfondimenti su progetti, trend e strumenti che aiutano davvero. In breve: contenuti utili per il tuo business. Niente chiacchiere. Niente spam quotidiano.',
    newsletterPlaceholder: 'Il vostro indirizzo e-mail',
    newsletterSuccess: "Grazie per l'iscrizione!",
    newsletterError: 'Si è verificato un errore.',
    navigation: 'Navigazione',
    contact: 'Contatto',
    navLinks: {
      leistungen: 'Servizi',
      projekte: 'Progetti',
      ueberUns: 'Chi siamo',
      kontakt: 'Contatto',
      blog: 'Blog'
    },
    legal: {
      datenschutz: 'Privacy',
      impressum: 'Impressum',
      agb: 'Termini',
      cookies: 'Cookie'
    }
  },
  en: {
    projectQuestion: 'Have a project in mind?',
    letsTalk: "Let's Talk",
    description: 'Swiss Made Web Solutions – Professional websites Switzerland, SEO and online marketing for SMEs in DACH region, Mallorca and Italy.',
    newsletter: 'Newsletter',
    newsletterDesc: 'From time to time we share tips on marketing, websites, visibility and branding — explained simply and directly applicable. Plus insights into projects, trends and tools that really help. In short: useful stuff for your business. No blabla. No daily bombardment.',
    newsletterPlaceholder: 'Your email address',
    newsletterSuccess: 'Thank you for subscribing!',
    newsletterError: 'An error occurred.',
    navigation: 'Navigation',
    contact: 'Contact',
    navLinks: {
      leistungen: 'Services',
      projekte: 'Projects',
      ueberUns: 'About Us',
      kontakt: 'Contact',
      blog: 'Blog'
    },
    legal: {
      datenschutz: 'Privacy Policy',
      impressum: 'Imprint',
      agb: 'Terms',
      cookies: 'Cookie Policy'
    }
  },
  es: {
    projectQuestion: '¿Tienes un proyecto en mente?',
    letsTalk: "Let's Talk",
    description: 'Swiss Made Web Solutions – Sitios web profesionales Suiza, SEO y marketing online para PYMES en la región DACH, Mallorca e Italia.',
    newsletter: 'Newsletter',
    newsletterDesc: 'De vez en cuando compartimos consejos sobre marketing, sitios web, visibilidad y branding — explicados de forma sencilla y directamente aplicables. Además, información sobre proyectos, tendencias y herramientas que realmente ayudan. En resumen: contenido útil para tu negocio. Sin bla bla. Sin bombardeo diario.',
    newsletterPlaceholder: 'Su correo electrónico',
    newsletterSuccess: '¡Gracias por suscribirse!',
    newsletterError: 'Ocurrió un error.',
    navigation: 'Navegación',
    contact: 'Contacto',
    navLinks: {
      leistungen: 'Servicios',
      projekte: 'Proyectos',
      ueberUns: 'Sobre nosotros',
      kontakt: 'Contacto',
      blog: 'Blog'
    },
    legal: {
      datenschutz: 'Privacidad',
      impressum: 'Aviso legal',
      agb: 'Términos',
      cookies: 'Política de cookies'
    }
  }
};

export default function Footer() {
  const { language } = useLanguage();
  const text = footerText[language] || footerText.de;
  const navigate = useNavigate();
  const location = useLocation();
  
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Navigate to homepage and scroll to section
  const navigateToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.querySelector(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.querySelector(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    setNewsletterStatus(null);

    try {
      await axios.post(`${API}/newsletter`, { email, language });
      setNewsletterStatus({ type: 'success', message: text.newsletterSuccess });
      setEmail('');
    } catch (err) {
      setNewsletterStatus({ type: 'error', message: text.newsletterError });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#0a0a0a] text-white">
      {/* CTA Section - Dyson Style */}
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div>
              <span className="text-white/50 text-sm uppercase tracking-wider mb-4 block">
                {text.projectQuestion}
              </span>
              <h3 className="text-3xl md:text-5xl font-bold text-white font-['Manrope']">
                {text.letsTalk}
              </h3>
            </div>
            <a
              href="#kontakt"
              onClick={(e) => {
                e.preventDefault();
                navigateToSection('#kontakt');
              }}
              className="inline-flex items-center justify-center w-32 h-32 md:w-40 md:h-40 rounded-full bg-white text-[#0a0a0a] font-bold text-lg hover:scale-105 transition-transform"
              data-testid="footer-cta"
            >
              LET'S TALK
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand & Newsletter */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl font-bold tracking-tight text-white font-['Manrope']">
                easywebswiss.ch
              </span>
              {/* Swiss Flag */}
              <svg className="w-6 h-6" viewBox="0 0 32 32">
                <rect fill="#FF0000" width="32" height="32"/>
                <rect fill="#FFFFFF" x="13" y="6" width="6" height="20"/>
                <rect fill="#FFFFFF" x="6" y="13" width="20" height="6"/>
              </svg>
            </div>
            <p className="text-white/80 mb-8 max-w-md">
              {text.description}
            </p>

            {/* Social Links - Dyson Style */}
            <div className="flex gap-6 mb-8">
              <a 
                href="https://www.instagram.com/easywebswiss.ch/?hl=de" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white/80 hover:text-white transition-colors uppercase text-sm font-medium"
                aria-label="Folge uns auf Instagram"
              >
                Instagram
              </a>
              <a 
                href="https://www.linkedin.com/company/easywebswiss/about/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white/80 hover:text-white transition-colors uppercase text-sm font-medium"
                aria-label="Besuche uns auf LinkedIn"
              >
                LinkedIn
              </a>
            </div>

            {/* Newsletter */}
            <div>
              <h4 className="text-white font-bold mb-3 font-['Manrope']">{text.newsletter}</h4>
              <p className="text-white/60 text-sm mb-4 leading-relaxed">{text.newsletterDesc}</p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={text.newsletterPlaceholder}
                  className="flex-1 bg-transparent border-b border-white/30 py-3 text-white focus:border-white focus:outline-none transition-colors placeholder:text-white/50"
                  data-testid="newsletter-email"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-white text-[#0a0a0a] rounded-full px-6 py-3 font-medium hover:bg-white/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="newsletter-submit"
                  aria-label="Newsletter abonnieren"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
              {newsletterStatus && (
                <p className={`mt-2 text-sm ${newsletterStatus.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                  {newsletterStatus.message}
                </p>
              )}
            </div>
          </div>

          {/* Navigation + Blog */}
          <div>
            <h4 className="text-white font-bold mb-6 font-['Manrope']">{text.navigation}</h4>
            <ul className="space-y-3">
              <li>
                <a href="#leistungen" onClick={(e) => { e.preventDefault(); navigateToSection('#leistungen'); }} className="footer-link text-sm">
                  {text.navLinks.leistungen}
                </a>
              </li>
              <li>
                <a href="#projekte" onClick={(e) => { e.preventDefault(); navigateToSection('#projekte'); }} className="footer-link text-sm">
                  {text.navLinks.projekte}
                </a>
              </li>
              <li>
                <a href="#team" onClick={(e) => { e.preventDefault(); navigateToSection('#team'); }} className="footer-link text-sm">
                  {text.navLinks.ueberUns}
                </a>
              </li>
              <li>
                <a href="#kontakt" onClick={(e) => { e.preventDefault(); navigateToSection('#kontakt'); }} className="footer-link text-sm">
                  {text.navLinks.kontakt}
                </a>
              </li>
              <li><Link to="/blog" onClick={() => window.scrollTo(0, 0)} className="footer-link text-sm">{text.navLinks.blog}</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold mb-6 font-['Manrope']">{text.contact}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-white/50 flex-shrink-0 mt-0.5" />
                <span className="text-white/80 text-sm">
                  Breitenstrasse 20<br />
                  5015 Erlinsbach SO<br />
                  {language === 'de' ? 'Schweiz' : language === 'it' ? 'Svizzera' : language === 'es' ? 'Suiza' : 'Switzerland'}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-white/50" />
                <a href="mailto:info@easywebswiss.ch" className="footer-link text-sm">
                  info@easywebswiss.ch
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-white/50" />
                <a href="tel:+41783202461" className="footer-link text-sm">
                  +41 78 320 24 61
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Instagram className="w-5 h-5 text-white/50" />
                <a href="https://www.instagram.com/easywebswiss.ch/?hl=de" target="_blank" rel="noopener noreferrer" className="footer-link text-sm">
                  @easywebswiss.ch
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Giant Brand Name - Dyson Style */}
        <div className="mt-16 md:mt-24 flex items-center justify-between">
          <span className="text-6xl md:text-8xl lg:text-9xl font-bold text-white/10 font-['Manrope'] tracking-tight">
            EASYWEB
          </span>
          <button
            onClick={scrollToTop}
            className="w-12 h-12 border border-white/30 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
            aria-label="Back to top"
          >
            <ArrowUp className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3 text-white/50 text-sm">
              <span>© {new Date().getFullYear()} easywebswiss.ch</span>
              <span>·</span>
              <span>UID: CHE-422.954.335</span>
              <span>·</span>
              <span>Swiss Made</span>
            </div>
            
            <div className="flex gap-6">
              <Link to="/datenschutz" onClick={() => window.scrollTo(0, 0)} className="text-white/50 hover:text-white transition-colors text-sm">{text.legal.datenschutz}</Link>
              <Link to="/impressum" onClick={() => window.scrollTo(0, 0)} className="text-white/50 hover:text-white transition-colors text-sm">{text.legal.impressum}</Link>
              <Link to="/agb" onClick={() => window.scrollTo(0, 0)} className="text-white/50 hover:text-white transition-colors text-sm">{text.legal.agb}</Link>
              <Link to="/cookies" onClick={() => window.scrollTo(0, 0)} className="text-white/50 hover:text-white transition-colors text-sm">{text.legal.cookies}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
